import { useState, useEffect } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useAuth } from "./AuthContext";
import { useNavigate, Link } from "react-router-dom";
import styles from "./auth/Auth.module.css";
import ApiClient from "./utils/apiClient";
import type { ApiError } from "./utils/apiClient";
import { toast } from 'react-toastify';
import { homePathForRole } from "./utils/roleHome";
export default function Login() {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showResendButton, setShowResendButton] = useState(false);
    const { isLoggedIn, login, logout, user, userRole } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const sessionExpired = localStorage.getItem('sessionExpired');
        if (sessionExpired) {
            setError('Deine Sitzung ist abgelaufen. Bitte melde Dich erneut an.');
            localStorage.removeItem('sessionExpired');
        }
    }, []);

    useEffect(() => {
        if (isLoggedIn && userRole) {
            // Rollenbasierte Weiterleitung auf die jeweilige Kursliste
            const home = homePathForRole(userRole);
            if (home) {
                localStorage.removeItem('lastPath');
                navigate(home);
            } else {
                // Fallback zur vorherigen Logik für andere Rollen oder wenn keine Rolle definiert ist
                const lastPath = localStorage.getItem('lastPath') || '/';
                localStorage.removeItem('lastPath');
                navigate(lastPath);
            }
        }
    }, [isLoggedIn, navigate, userRole]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
        setShowResendButton(false);
    };

    const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setShowResendButton(false);

        try {
            // login() wirft nicht, sondern liefert {success, message} — wie in
            // MiniLogin über den switch unten leiten, sonst landet z. B.
            // „Email not confirmed“ roh und ohne „erneut senden“-Knopf im Formular.
            const result = await login(formData.email.toLowerCase().trim(), formData.password);
            if (!result.success) {
                throw new Error(result.message || 'Ein Fehler ist aufgetreten');
            }
            toast.success("Login erfolgreich");
        } catch (caught) {
            const error = caught as ApiError;

            switch (error.message) {
                case "Invalid credentials":
                    setError('Ungültige E-Mail oder Passwort');
                    toast.error("Ungültige E-Mail oder Passwort");
                    break;
                case "User not found":
                    setError('Kein Account mit dieser E-Mail gefunden');
                    toast.info("Kein Account mit dieser E-Mail gefunden");
                    break;
                case "Email not confirmed":
                    setError('Bitte bestätige zuerst deine E-Mail-Adresse');
                    toast.info("Bitte bestätige zuerst deine E-Mail-Adresse");
                    setShowResendButton(true);
                    break;
                default:
                    setError(error.message || 'Ein Fehler ist aufgetreten');
            }
        } finally {
            setIsLoading(false);


        }
    };

    const handleResendConfirmation = async () => {
        try {
            await ApiClient.post('auth/resend-confirmation', {
                email: formData.email,
                password: formData.password
            });
            setError('Bestätigungs-Link wurde erneut gesendet');
            setShowResendButton(false);
        } catch (caught) {
            const err = caught as ApiError;
            setError(err.originalErrorData?.detail || err.message || 'Fehler beim Senden des Bestätigungs-Links');
        }
    };

    if (isLoggedIn) {
        return (
            <div className={styles.authContainer}>
                <div className={styles.authCard}>
                    <h2>Willkommen zurück!</h2>
                    <p>Du bist eingeloggt als {user?.username}</p>
                    <button onClick={logout} className={styles.button}>
                        Ausloggen
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.authContainer}>
            <div className={styles.authCard}>
                <h2>Anmelden</h2>
                <form onSubmit={handleLogin}>
                    <div className={styles.inputGroup}>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="E-Mail"
                            required
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Passwort"
                            required
                        />
                    </div>

                    {error && <div className={styles.error}>{error}</div>}

                    {showResendButton && (
                        <button
                            type="button"
                            onClick={handleResendConfirmation}
                            className={styles.secondaryButton}
                        >
                            Bestätigungs-Link erneut senden
                        </button>
                    )}

                    <button
                        type="submit"
                        disabled={isLoading}
                        className={styles.primaryButton}
                    >
                        {isLoading ? 'Wird angemeldet...' : 'Anmelden'}
                    </button>

                    <div className={styles.authLinks}>
                        <Link to="/register">Noch kein Account? Registrieren</Link>
                        <Link to="/forgot-password">Passwort vergessen?</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
